import React, {useCallback, useEffect} from 'react';
import {useFormik} from "formik";
import * as Yup from "yup";
import {StyledButton} from "../../ui-base/button";
import {Grid} from "@mui/material";
import {AddBookForm} from "./add-book-form";
import {RequireInfo} from "../../ui-base/require-info";
import {setProgressValue, showSnackSuccess, toggleLoader, toggleProgress} from "../../../store/global/global-slice";
import {useDispatch} from "react-redux";
import {useHistory, useParams} from "react-router-dom";
import {PublisherService} from "../../../services/publisher.service";
import {PublisherSimilarBookModel} from "../../../models/publisher/publisher-similar-book.model";
import {BaseService} from "../../../services/base.service";
import {useTranslation} from "react-i18next";

const AddBookSchema = Yup.object().shape({
    title: Yup.string().required(),
    authors: Yup.string().required(),
    isbn: Yup.string().required(),
});

export const EditBook: React.FC<any> = () => {
    const [book, setBook] = React.useState<PublisherSimilarBookModel>(null);
    const [contentFile, setContentFile] = React.useState(null);
    const [contentCover, setContentCover] = React.useState(null);
    const dispatch = useDispatch();
    const history = useHistory();
    const urlParams = useParams();
    const id: string = urlParams['id'] as string;
    const { t } = useTranslation(['publisher', 'common']);

    const form = useFormik({
        initialValues: {
            title: '',
            authors: '',
            isbn: '',
            cover: null,
            fullContent: null
        },
        validationSchema: AddBookSchema,
        onSubmit: async values => {
            if(!checkFileError()) {
                dispatch(toggleLoader(true));

                const bookRes = await PublisherService.editPublisherBook(
                    book.id,
                    values.title,
                    values.authors,
                    values.isbn
                );

                if (bookRes.status) {
                    dispatch(toggleProgress(true));

                    let fullContent = values.fullContent || '';
                    let cover = values.cover || '';

                    if (!fullContent && book.content_full && book.content_full.file && !contentFile) {
                        fullContent = '';
                    } else if (!fullContent && book.content_full && book.content_full.file && contentFile) {
                        fullContent = null;
                    }

                    if (!cover && book.cover && book.cover.file && !contentCover) {
                        cover = '';
                    } else if (!cover && book.cover && book.cover.file && contentCover) {
                        cover = null;
                    }

                    const attachmentRes = await PublisherService.uploadFilesBook(
                        book.id,
                        fullContent,
                        cover,
                        (progress) => {
                            dispatch(setProgressValue(progress))
                        }
                    )

                    dispatch(toggleProgress(false));
                    dispatch(toggleLoader(false));

                    if (attachmentRes) {
                        dispatch(showSnackSuccess(t('editBook.bookEdited')));
                        history.push('/publisher/similar-books/list');
                    }
                } else {
                    dispatch(toggleLoader(false));
                }
            }
        }
    });

    const checkFileError = () => {
        if (form.touched.fullContent && !form.values.fullContent && !contentFile) {
            return true;
        } else {
            return false;
        }
    }

    const loadBook = useCallback(async (id: string) => {
        dispatch(toggleLoader(true));
        const bookRes = await PublisherService.getPublisherBook(id);

        if (bookRes) {
            setBook(bookRes);
            form.setFieldValue('title', bookRes.title);
            form.setFieldValue('authors', bookRes.authors);
            form.setFieldValue('isbn', bookRes.isbn);

            setContentFile(BaseService.getFileName(bookRes.content_full?.file_purpose, bookRes.content_full?.file));
            setContentCover(BaseService.getFileName(bookRes.cover?.file_purpose, bookRes.cover?.file));
        }

        dispatch(toggleLoader(false));
    }, [dispatch, form, setContentFile, setContentCover]);

    useEffect(() => {
        if (id) {
            loadBook(id);
        }
    }, [id]);

    return (
        <form onSubmit={form.handleSubmit} style={{width: '100%'}} id="add-manuscript-form">
            <Grid container direction={"column"} spacing={1}>
                <Grid item container justifyContent={"flex-end"}>
                    <StyledButton type={"submit"} style={{marginRight: '1rem'}}>{t('common:save')}</StyledButton>
                </Grid>

                <Grid item>
                    <AddBookForm
                        form={form}
                        fileError={checkFileError()}
                        contentFile={contentFile}
                        setContentFile={setContentFile}
                        contentCover={contentCover}
                        setContentCover={setContentCover}
                    />
                </Grid>
            </Grid>
            <RequireInfo/>
        </form>
    );
}
